import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;

        const initAuth = async () => {
            try {
                // Pega a sessão inicial
                const { data: { session } } = await supabase.auth.getSession();

                if (mounted) {
                    if (session?.user) {
                        setUser(session.user);
                        // Tenta carregar o perfil sem travar o resto do código
                        loadUserProfile(session.user.id);
                    } else {
                        setLoading(false);
                    }
                }
            } catch (err) {
                console.error('useAuth: Erro na inicialização:', err);
                if (mounted) setLoading(false);
            }
        };

        initAuth();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (!mounted) return;
            const currentUser = session?.user || null;
            setUser(currentUser);

            if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
                loadUserProfile(currentUser?.id);
            } else if (event === 'SIGNED_OUT') {
                setProfile(null);
                setLoading(false);
            }
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    const loadUserProfile = async (userId, retryCount = 0) => {
        if (!userId) {
            setLoading(false);
            return;
        }

        try {
            const { data, error } = await supabase
                .from('usuarios')
                .select('*, portao:portoes(nome)')
                .eq('id', userId)
                .maybeSingle();

            if (error) {
                console.warn(`useAuth: Erro ao carregar perfil (Tentativa ${retryCount + 1}):`, error.message);

                // Se for o erro de recursão, tenta mais uma vez após 1.5s (tempo do banco limpar o cache de RLS)
                if (retryCount < 2 && error.message.includes('recursion')) {
                    setTimeout(() => loadUserProfile(userId, retryCount + 1), 1500);
                    return;
                }
                setProfile(null);
            } else if (data) {
                console.log('useAuth: Perfil carregado com sucesso!');
                setProfile(data);
            }
        } catch (e) {
            console.error('useAuth: Falha crítica perfil:', e);
        } finally {
            // Se for a última tentativa ou sucesso, libera o loading
            if (retryCount >= 2 || !userId) setLoading(false);
            // Fallback: Se após 5s ainda estiver loading, liberamos de qualquer jeito
            setTimeout(() => setLoading(false), 5000);
        }
    };

    const value = {
        user,
        profile,
        loading,
        signIn: (e, p) => supabase.auth.signInWithPassword({ email: e, password: p }),
        signUp: (e, p, d) => supabase.auth.signUp({
            email: e,
            password: p,
            options: {
                data: {
                    nome_completo: d.nome_completo,
                    telefone: d.telefone,
                    placa_veiculo: d.placa_veiculo
                }
            }
        }),
        signOut: () => supabase.auth.signOut(),
        isAuthenticated: !!user,
        isParent: profile?.tipo === 'responsavel',
        isSchool: profile?.tipo === 'escola',
        isDirector: profile?.perfil === 'diretor',
        isMonitor: profile?.perfil === 'monitor'
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
